"use client";

import { useMemo, useRef } from "react";
import Image from "next/image";
import { FileText, ImageIcon, ImagePlus, X } from "lucide-react";
import { useWebSiteContext } from "@core/helpers/client/contexts/WebSiteContext";
import { file_note, to_latin_digits } from "@core/helpers/client/form-renderer/site_helpers";
import CheckBox from "@/components/ui/CheckBox";
import SelectBoxInput from "@/components/ui/SelectBoxInput";
import TextInput from "@/components/ui/TextInput";
import TextareaInput from "@/components/ui/TextareaInput";
import RadioCard from "@/components/ui/RadioButton/RadioCard";
import { RadioGroup } from "@/components/ui/RadioButton/RadioGroup";

// نوع‌های ثابت فرم‌ساز که core با خود اینپوت‌های ژومنت (TextInput، TextareaInput، SelectBoxInput، CheckBox، RadioCard) می‌کشد،
// پس ظاهرشان همان اینپوت‌های بقیه‌ی سایت است. فیلد را core نگه می‌دارد (مقدار، شرط‌ها، بررسی‌ها، خطاها) و هر کامپوننت فقط شکل
// آن را می‌دهد: label و گزینه‌ها و مقدار را از props می‌گیرد و با onChange (یا on_text و on_file) به core برمی‌گرداند.
// مدیر هر نوعی را می‌تواند در هر فرمی بگذارد، پس هیچ کامپوننتی این‌جا فرض فرم خاصی ندارد: برچسب، راهنما، placeholder، تعداد رقم،
// فرمت‌ها و حجم فایل همه از فیلد (پنل) و از core می‌آیند. محدودیت مخصوص یک فرم (مثلا فقط تصویر برای لوگو) را همان فرم می‌دهد.

export const SELECT_BOX_STYLE = "[&>button]:min-h-[41px] [&>[role=combobox]]:min-h-[41px]";

/**
 * بعد از باز شدن منوی یک سلکت‌باکس، صفحه را طوری اسکرول می‌کند که تا آخرین آیتم دیده شود.
 * نوار ثابت پایین موبایل (--page-fixed-footer-height) هم در نظر گرفته می‌شود.
 * @param {import("react").MouseEvent} event کلیکی که از داخل فیلد بالا آمده است
 */
export const reveal_open_menu = (event) => {
	const container = event.target?.closest?.('div[dir="rtl"]');
	if (!container) return;

	setTimeout(() => {
		const list = container.querySelector("ul");
		const menu = list?.parentElement;
		if (!menu || menu === container) return;

		const footer = parseInt(getComputedStyle(container).getPropertyValue("--page-fixed-footer-height"), 10) || 0;
		menu.style.scrollMarginBottom = `${footer + 16}px`;
		menu.scrollIntoView({ block: "nearest", behavior: "smooth" });
	}, 50);
};

const FieldError = ({ error }) => (error ? <p className="mt-1.5 text-xs text-jmt-red-600">{error}</p> : null);
export const FieldHint = ({ text }) => (text ? <p className="mt-1.5 text-xs text-jmt-neutral-500">{text}</p> : null);

// برچسب بالای گروه‌های رادیویی و چک‌باکسی (مثل label بقیه‌ی اینپوت‌ها)
const GroupLabel = ({ label, required }) => (
	<p className="mb-1.5 min-h-5 text-sm font-medium text-jmt-main">
		<span className="relative">
			{label}
			{required && <span className="absolute top-0 -left-2.5 text-red-600">*</span>}
		</span>
	</p>
);

// ---- متن، عدد، قیمت، موبایل، ایمیل، کدملی، کدپستی و متن بلند ----

const SiteTextField = ({ field, error, disabled, required, id, text, on_text, input, unit }) => {
	const is_ltr = input?.dir === "ltr";
	// فیلدی که فقط رقم می‌گیرد (کدها، الگوی فقط‌رقم مدیر، عدد صحیح با سقف) و حداکثر چند رقم؛ از core
	const max_digits = input?.max_digits;

	const handle_change = (event) => {
		let value = event.target.value;
		// ارقام فارسی و عربی در فیلد لاتین و عددی انگلیسی می‌شوند تا بررسی‌ها همان را ببینند
		if (is_ltr || max_digits) value = to_latin_digits(value);
		if (max_digits) value = value.replace(/\D/g, "").slice(0, max_digits);
		on_text(value);
	};

	if (field.type === "textarea") {
		return (
			<div className="mb-7">
				<TextareaInput
					id={id}
					name={id}
					label={field.label}
					required={required}
					disabled={disabled}
					rows={input?.rows}
					maxLength={input?.max_length}
					placeholder={input?.placeholder}
					value={text}
					onChange={handle_change}
					errors={error ? [error] : undefined}
				/>
				<FieldHint text={field.hint} />
			</div>
		);
	}

	return (
		<TextInput
			id={id}
			name={id}
			label={field.label}
			required={required}
			disabled={disabled}
			value={text}
			onChange={handle_change}
			errors={error ? [error] : undefined}
			type={input?.type}
			inputMode={max_digits ? "numeric" : input?.input_mode}
			dir={input?.dir}
			input_style={is_ltr ? "text-left" : undefined}
			autoComplete={input?.autocomplete}
			maxLength={input?.max_length}
			placeholder={input?.placeholder}
			description={field.hint}
			descriptionClassName="text-jmt-neutral-500"
			endAdornment={unit}
		/>
	);
};

// ---- انتخاب، استان و شهر: SelectBoxInput تک‌انتخابی ----

const SEARCHABLE_FROM = 8;

const SiteSelectField = ({ field, value, onChange, error, disabled, required, id, options, loading }) => {
	const select_options = useMemo(() => (options || []).map((option) => ({ value: option.value, label: option.title })), [options]);
	const current = value && typeof value === "object" ? value.value : value;
	const is_geo = field.type === "state" || field.type === "city";
	// استان و شهر و هر فهرست بلند جست‌وجو دارد
	const searchable = is_geo || select_options.length > SEARCHABLE_FROM;

	return (
		<div className="mb-7" onClick={reveal_open_menu}>
			<SelectBoxInput
				id={id}
				name={id}
				label={field.label}
				required={required}
				searchable={searchable}
				placeholder={field.placeholder || "انتخاب"}
				disabled={disabled || loading || (field.type === "city" && select_options.length === 0)}
				options={select_options}
				value={current ?? ""}
				onChange={(event) => {
					const found = (options || []).find((option) => option.value === String(event.target.value));
					onChange(found ? { title: found.title, value: found.value } : undefined);
				}}
				errors={error ? [error] : undefined}
				input_style={SELECT_BOX_STYLE}
				auto_flip
			/>
			<FieldHint text={field.hint} />
		</div>
	);
};

// ---- چندانتخابی: SelectBoxInput با چیپ ----

const SiteMultiSelectField = ({ field, value, onChange, error, disabled, required, id, options }) => {
	const select_options = useMemo(() => (options || []).map((option) => ({ value: option.value, label: option.title })), [options]);
	const selected = useMemo(() => (Array.isArray(value) ? value.map((item) => item?.value) : []), [value]);

	return (
		<div className="mb-7" onClick={reveal_open_menu}>
			<SelectBoxInput
				id={id}
				name={id}
				label={field.label}
				required={required}
				disabled={disabled}
				multiple
				searchable
				auto_flip
				placeholder={field.placeholder || "انتخاب"}
				options={select_options}
				value={selected}
				onChange={(event) => {
					const list = (event.target.value || [])
						.map((item) => (options || []).find((option) => option.value === String(item)))
						.filter(Boolean)
						.map((option) => ({ title: option.title, value: option.value }));
					onChange(list.length > 0 ? list : undefined);
				}}
				errors={error ? [error] : undefined}
				input_style={SELECT_BOX_STYLE}
			/>
			<FieldHint text={field.hint} />
		</div>
	);
};

// ---- رادیویی (و دکمه‌های چندحالته): RadioCard ----

const SiteRadioField = ({ field, value, onChange, error, disabled, required, id, options }) => {
	const current = value && typeof value === "object" ? value.value : value;

	return (
		<div id={id} className="mb-7">
			<GroupLabel label={field.label} required={required} />
			<RadioGroup
				value={current}
				onChange={(next) => {
					const found = (options || []).find((option) => option.value === next);
					if (found && !disabled) onChange({ title: found.title, value: found.value });
				}}
				className="gap-2!"
			>
				{(options || []).map((option) => (
					<RadioCard key={option.value} value={option.value} title={option.title} defaultBorder checkedBorder disabled={disabled} />
				))}
			</RadioGroup>
			<FieldHint text={field.hint} />
			<FieldError error={error} />
		</div>
	);
};

// ---- چک‌باکس چندگزینه‌ای: CheckBox ----

const SiteCheckboxField = ({ field, value, onChange, error, disabled, required, id, options }) => {
	const selected = Array.isArray(value) ? value.map((item) => item?.value) : [];

	const toggle = (option) => {
		const next = selected.includes(option.value)
			? (value || []).filter((item) => item?.value !== option.value)
			: [...(value || []), { title: option.title, value: option.value }];
		onChange(next.length > 0 ? next : undefined);
	};

	return (
		<div id={id} className="mb-7">
			<GroupLabel label={field.label} required={required} />
			<div className="flex flex-col gap-3">
				{(options || []).map((option) => (
					<CheckBox key={option.value} label={option.title} checked={selected.includes(option.value)} disabled={disabled} onChange={() => toggle(option)} />
				))}
			</div>
			<FieldHint text={field.hint} />
			<FieldError error={error} />
		</div>
	);
};

// ---- آپلود فایل: جعبه‌ی خط‌چین با پیش‌نمایش، نام فایل و دکمه‌ی حذف؛ انتخاب و بررسی فایل با core است (on_file) ----

// فرمت‌ها و حجم را core می‌دهد (accept، max_file_bytes)؛ فرمی که فرمت کمتری می‌خواهد accept خودش را می‌دهد و on_file همان را
// اجرا می‌کند. متن زیر جعبه راهنمای فیلد در پنل است، وگرنه فرمت‌ها و حجم (file_note از core).
export const SiteFileField = ({ field, value, onChange, error, disabled, required, id, lang, on_file, accept, max_file_bytes }) => {
	const input_ref = useRef(null);
	const { web_site_setting } = useWebSiteContext() || {};
	// فایل تازه‌انتخاب‌شده data URL دارد؛ فایلی که قبلا ثبت شده {url, name} است و آدرسش نسبی است (با پیشوند استوریج کامل می‌شود)
	const stored_url = typeof value?.url === "string" && value.url ? value.url : "";
	const prefix = web_site_setting?.web_site_storage_prefix_url;
	const resolved_url = !stored_url || /^(https?:|data:|blob:|\/)/.test(stored_url) ? stored_url : prefix ? `${prefix.replace(/\/+$/, "")}/${stored_url.replace(/^\/+/, "")}` : "";
	const has_value = Boolean(value);
	// پیش‌نمایش فقط برای تصویر؛ PDF و DOCX آیکون فایل می‌گیرند
	const is_image_file = /\.(png|jpe?g|webp|gif)$/i.test(value?.file_name || value?.name || "");
	const preview = typeof value?.content === "string" && value.content.startsWith("data:image") ? value.content : is_image_file ? resolved_url : "";

	return (
		<div className="relative pb-1">
			<GroupLabel label={field.label} required={required} />

			<div className={`flex items-center gap-3 rounded-md border border-dashed p-3 ${error ? "border-red-500" : "border-jmt-neutral-300"}`}>
				<button
					type="button"
					id={id}
					disabled={disabled}
					onClick={() => input_ref.current?.click()}
					aria-label={has_value ? "تغییر فایل" : "انتخاب فایل"}
					className="flex size-16 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-md bg-jmt-neutral-100 text-jmt-neutral-500"
				>
					{preview ? (
						<Image src={preview} unoptimized alt="" width={64} height={64} className="size-16 object-contain" />
					) : has_value && is_image_file ? (
						<ImageIcon size={24} strokeWidth={1.75} aria-hidden="true" />
					) : has_value ? (
						<FileText size={24} strokeWidth={1.75} aria-hidden="true" />
					) : (
						<ImagePlus size={24} strokeWidth={1.75} aria-hidden="true" />
					)}
				</button>

				<div className="min-w-0 flex-1">
					<p className="truncate text-sm text-jmt-main" dir="auto">
						{value?.file_name || value?.name || (has_value ? "فایل انتخاب‌شده" : "فایلی انتخاب نشده است")}
					</p>
					<p className="mt-1 text-xs text-jmt-neutral-500">{field.hint || file_note(accept, max_file_bytes, lang)}</p>
				</div>

				{has_value && (
					<button
						type="button"
						disabled={disabled}
						onClick={() => onChange(undefined)}
						aria-label="حذف فایل"
						className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-jmt-neutral-500 hover:bg-jmt-neutral-100"
					>
						<X size={18} strokeWidth={1.75} aria-hidden="true" />
					</button>
				)}

				<input
					ref={input_ref}
					type="file"
					accept={accept}
					className="hidden"
					onChange={(event) => {
						const file = event.target.files?.[0];
						event.target.value = "";
						on_file(file, { accept });
					}}
				/>
			</div>

			<div className="mt-1.5 min-h-4 text-xs text-jmt-red-600">{error}</div>
		</div>
	);
};

// انواع ثابتی که core با اینپوت‌های ژومنت می‌کشد (field_components). تاریخ همان فیلد پیش‌فرض core می‌ماند، چون ژومنت اینپوت تاریخ ندارد.
export const SITE_FIELD_COMPONENTS = {
	text: SiteTextField,
	textarea: SiteTextField,
	number: SiteTextField,
	price: SiteTextField,
	mobile: SiteTextField,
	email: SiteTextField,
	national_code: SiteTextField,
	postal_code: SiteTextField,
	select: SiteSelectField,
	state: SiteSelectField,
	city: SiteSelectField,
	multi_select: SiteMultiSelectField,
	radio: SiteRadioField,
	toggle: SiteRadioField,
	checkbox: SiteCheckboxField,
	file: SiteFileField,
};
